import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";

import {
  Button,
  Divider,
  Input,
  SreenCoverLoading,
  usePopupPage,
  useRadioBoxGroup,
} from "src/components";
import { recoilStates } from "src/recoil/recoilStates";
import styled from "styled-components";
import { TeamSubmitDataType } from "src/types";
import { MemberSearchPopupContents } from "src/components/PopupPageContents";
import YearBookingOptionsPopupContents from "src/components/PopupPageContents/YearBookingOptionsPopupContents";
import { dateFormat, parseJsonStr, digit4ToTimeString } from "src/utils";
import { useNavigate } from "react-router-dom";
import { createTeeshotAxiosInstance } from "src/http";

import { useRefetchBookings } from "src/hooks";
import AlertModal from "src/components/YearBooking/components/common/AlertModal";
import ActionModal from "src/components/YearBooking/components/common/ActionModal";
import { usePopupSelectV2 } from "src/components/PopupSelect";

export default function YearGroupTeamRegister() {
  const navigate = useNavigate();

  const [group, setGroup] = useRecoilState(recoilStates.group);

  const [bookingApplication, setBookingApplication] = useRecoilState(
    recoilStates.bookingApplication
  );
  const clubRelData = bookingApplication?.clubRels?.find((cr) =>
    [4, 6].includes(cr.year_booking_status_id)
  );
  const rounds = clubRelData?.year_booking_round_dates;

  const {
    PopupSelect: DatePopupSelect,
    activeIds: activeDateIds,
    propsToInject,
  } = usePopupSelectV2({
    defaultSelectIds: [
      rounds
        ?.filter((r) => {
          return new Date(new Date(r.round_date).getTime() + 86400 * 1000) > new Date();
        })
        .reduce(
          (acc: any, curr: any) => {
            if (new Date(curr.round_date) < new Date(acc.round_date)) {
              return curr;
            }
            return acc;
          },
          { round_date: new Date(19999999999999) }
        )?.id || 0,
    ],
  });

  const [teams, setTeams] = useState<TeamSubmitDataType>();
  const [isDateExpanded, setIsDateExpanded] = useState(false);
  const [selectedRoundId, setSelectedRoundId] = useState<number | undefined>(undefined);

  const [forcedRerenderFlag, setForcedRerenderFlag] = useState(true);

  const effectiveSelectedRoundId = selectedRoundId ?? activeDateIds?.[0];
  const currentRound = rounds?.find((r) => r.id === effectiveSelectedRoundId);
  const hasSavedData = Boolean(currentRound?.is_temp_saved) || Boolean(currentRound?.is_fixed);

  // 모든 멤버가 조에 배정되었는지 확인
  const isAllMembersAssigned =
    teams?.some((team) => team.members && team.members.length > 0) &&
    group?.members?.every((member) =>
      teams?.some((team) => team.members?.some((teamMember) => teamMember.member_id === member.id))
    );

  const { RadioBoxGroup: AwardsBoxGroup, activeIds: activeAwardsIds } = useRadioBoxGroup({
    multipleValue: true,
    boxType: "round",
    multiLineColumnCount: 4,
    defaultSelectedIds: hasSavedData
      ? parseJsonStr(currentRound?.award_ids_arr_json_str || "")?.map((id: any) => +id) || []
      : parseJsonStr(bookingApplication?.award_ids_arr_json_str || "")?.map((id: any) => +id) || [],
    // fontSize: "12px",
    items: [
      { id: 1, name: "우승" },
      { id: 2, name: "준우승" },
      { id: 3, name: "3위" },
      { id: 4, name: "행운상" },
      { id: 5, name: "특별상" },
      { id: 6, name: "메달리스트" },
      { id: 7, name: "롱게스트" },
      { id: 8, name: "니어리스트" },
      { id: 9, name: "매너상" },
      { id: 10, name: "베스트드레서", fontSize: "12px" },
    ],
  });

  const { RadioBoxGroup: MealBoxGroup, activeIds: mealActiveIds } = useRadioBoxGroup({
    boxType: "round",
    defaultSelectedIds: hasSavedData
      ? [currentRound?.meal_type_id || 4]
      : [bookingApplication?.meal_type_id || 4],
    items: [
      { id: 1, name: "조식" },
      { id: 2, name: "중식" },
      { id: 3, name: "석식" },
      { id: 4, name: "먹지않음" },
    ],
  });
  const { RadioBoxGroup: RoomBoxGroup, activeIds: roomActiveIds } = useRadioBoxGroup({
    boxType: "round",
    defaultSelectedIds: hasSavedData
      ? [currentRound?.room_type_id || 3]
      : [bookingApplication?.room_type_id || 3],
    items: [
      { id: 1, name: "라운드전" },
      { id: 2, name: "라운드후" },
      { id: 3, name: "이용하지 않아요" },
    ],
  });

  const { RadioBoxGroup: ShopActiveBoxGroup, activeIds: shopActiveIds } = useRadioBoxGroup({
    boxType: "round",

    defaultSelectedIds: hasSavedData
      ? [currentRound?.proshop_item ? 1 : 2]
      : [bookingApplication?.proshop_item ? 1 : 2],
    items: [
      { id: 1, name: "유" },
      { id: 2, name: "무" },
    ],
  });

  const { value: shopItemValue, onChange: handleShopItemValueChange } = Input.hooks.useInput(
    hasSavedData ? currentRound?.proshop_item : bookingApplication?.proshop_item || ""
  );
  const { value: etcValue, onChange: handleEtcValue } = Input.hooks.useInput("");

  // 옵션 팝업에서 사용할 상태 관리 함수들
  const handleAwardsChange = (awards: string[]) => {
    // 시상 요청 변경 시 부모 상태 업데이트
    console.log("Awards changed:", awards);
    // 임시저장 시에만 호출되므로 무한 루프 발생하지 않음
    // 강제 리렌더링으로 상태 업데이트
    setForcedRerenderFlag(false);
    setTimeout(() => {
      setForcedRerenderFlag(true);
    }, 0);
  };

  const handleMealChange = (meal: string) => {
    // 식사 변경 시 부모 상태 업데이트
    console.log("Meal changed:", meal);
    // 임시저장 시에만 호출되므로 무한 루프 발생하지 않음
    // 강제 리렌더링으로 상태 업데이트
    setForcedRerenderFlag(false);
    setTimeout(() => {
      setForcedRerenderFlag(true);
    }, 0);
  };

  const handleRoomChange = (room: string) => {
    // 룸이용 변경 시 부모 상태 업데이트
    console.log("Room changed:", room);
    // 임시저장 시에만 호출되므로 무한 루프 발생하지 않음
    // 강제 리렌더링으로 상태 업데이트
    setForcedRerenderFlag(false);
    setTimeout(() => {
      setForcedRerenderFlag(true);
    }, 0);
  };

  const handleProshopChange = (proshop: string) => {
    // 프로샵 이용 변경 시 부모 상태 업데이트
    console.log("Proshop changed:", proshop);
    // 임시저장 시에만 호출되므로 무한 루프 발생하지 않음
    // 강제 리렌더링으로 상태 업데이트
    setForcedRerenderFlag(false);
    setTimeout(() => {
      setForcedRerenderFlag(true);
    }, 0);
  };

  useEffect(() => {
    setForcedRerenderFlag(false);
    setTimeout(() => {
      setForcedRerenderFlag(true);
    }, 0);
  }, [effectiveSelectedRoundId]);

  useEffect(() => {
    const roundData = rounds?.find((r) => r.id === effectiveSelectedRoundId);

    if (!roundData?.teams || roundData?.teams?.length < 1) {
      setTeams([
        { team_number: 1, members: [] },
        { team_number: 2, members: [] },
        { team_number: 3, members: [] },
        { team_number: 4, members: [] },
        { team_number: 5, members: [] },
        { team_number: 6, members: [] },
        { team_number: 7, members: [] },
        { team_number: 8, members: [] },
        { team_number: 9, members: [] },
        { team_number: 10, members: [] },
        { team_number: 11, members: [] },
        { team_number: 12, members: [] },
        { team_number: 13, members: [] },
        { team_number: 14, members: [] },
        { team_number: 15, members: [] },
        { team_number: 16, members: [] },
        { team_number: 17, members: [] },
        { team_number: 18, members: [] },
        { team_number: 19, members: [] },
        { team_number: 20, members: [] },
      ]);
      return;
    }

    const teamData = roundData.teams.map((t) => {
      return {
        ...t,
        members: t?.year_booking_round_date_team_rel_booking_group_member?.map((m) => {
          return { ...m, member_id: m.booking_group_member_id };
        }),
      };
    });
    let teamN = 1;
    for (teamN; teamN < 21; teamN++) {
      if (teamData.some((td) => td.team_number === teamN)) {
        continue;
      }
      teamData.push({
        team_number: teamN,
        members: [],
      } as any);
    }
    teamData.sort((a, b) => a.team_number - b.team_number);
    setTeams(teamData as any);
  }, [bookingApplication, effectiveSelectedRoundId]);

  const {
    PopupComponent: MemeberSearchPopup,
    openPopup: openMemberSearchPopup,
    closePopup: closeMemberSearchPopup,
  } = usePopupPage("member-search");
  const {
    PopupComponent: OptionPopup,
    openPopup: openOptionPopup,
    closePopup: closeopenOptionPopup,
  } = usePopupPage("options");

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [currentSelectedTeamNumber, setCurrentSelectedTeamNumber] = useState(0);

  const refetchBookings = useRefetchBookings();
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [showActionModal, setShowActionModal] = useState(false);
  const handleSubmit = async (tempSave?: boolean) => {
    const notEmptyAddingTeams = teams?.filter((t) => {
      return t.members?.length && t.members?.length > 0;
    });
    if (!tempSave && (!effectiveSelectedRoundId || !notEmptyAddingTeams?.length)) {
      setAlertTitle("입력 오류");
      setAlertMessage("라운드 일자 및 팀 정보를 입력하세요");
      setShowAlertModal(true);
      return;
    }

    try {
      setIsSubmitLoading(true);
      const teamSubmitRes = await createTeeshotAxiosInstance().post(
        `/yearBooking/booking/team${tempSave ? "?tempSave=1" : ""}`,
        {
          submitData: {
            roundId: effectiveSelectedRoundId,
            teams: notEmptyAddingTeams,
            awardIds: activeAwardsIds?.length ? activeAwardsIds : null,
            meal_type_id: mealActiveIds?.[0] === 4 ? null : mealActiveIds?.[0],
            room_type_id: roomActiveIds?.[0] === 3 ? null : roomActiveIds?.[0],
            proshop_item: shopActiveIds?.[0] === 1 ? shopItemValue : null,
            etc: etcValue?.trim() ? etcValue?.trim() : null,
          },
        }
      );
      if (!teamSubmitRes.data.success) {
        throw new Error();
      }
      await refetchBookings();

      if (tempSave) {
        if (window.location.search.includes("options")) {
          setAlertTitle("(옵션 선택) 임시 저장");
          setAlertMessage("라운드 옵션 선택이 임시로 저장되었습니다.");
          setShowAlertModal(true);
          // 옵션 팝업 닫기
          closeopenOptionPopup();
        } else {
          setAlertTitle("(조 편성) 임시 저장");
          setAlertMessage("조 편성이 임시로 저장되었습니다.");
          setShowAlertModal(true);
        }
      } else {
        setAlertTitle("등록 완료!");
        setAlertMessage("골프장에 전송되었습니다.");
        setShowAlertModal(true);
      }
      if (!tempSave) {
        // navigate("/year-booking/roundPayment");
        navigate("/year-booking");
      }
    } catch (e) {
      console.log("e :", e);
      setIsSubmitLoading(false);
      setAlertTitle("오류");
      setAlertMessage("팀 등록 실패");
      setShowAlertModal(true);
      window.location.reload();
    } finally {
      setIsSubmitLoading(false);
    }

    return;
  };
  if (!forcedRerenderFlag) {
    return <></>;
  }
  return (
    <>
      {isSubmitLoading ? <SreenCoverLoading /> : null}
      <Container className="flex-1 d-flex fd-c gap-1">
        <div className="content flex-1 d-flex fd-c gap-3">
          <div className="info-notice">
            <p className="notice-text">
              라운드 3일 전까지 라운드 일자별 조를 편성해 제출해주세요.
              <br />
              한번 제출된 조편성은 변경이 어려우니 신중을 기해주세요.
            </p>
          </div>
          <Text>라운드 일자</Text>
          {/* 가장 가까운 라운드 일자만 표시 */}
          <div className="jo1-dropdown">
            <div className="dropdown-container">
              <div className="dropdown-header">
                <div className="header-content">
                  <div className="date-info">
                    <span className="date-text">
                      {currentRound
                        ? `${dateFormat(
                            new Date(currentRound.round_date)
                          )}요일 ${digit4ToTimeString(clubRelData?.start_time || "")}`
                        : "부킹일자를 선택하세요."}
                    </span>
                    {currentRound && (
                      <span className="round-text">
                        {(rounds?.findIndex((r) => r.id === currentRound.id) ?? 0) + 1}회차
                      </span>
                    )}
                  </div>
                  {currentRound && (
                    <div className="status-info">
                      <span
                        className={
                          currentRound.is_fixed
                            ? "status-completed"
                            : currentRound.is_temp_saved
                            ? "status-incomplete"
                            : "status-incomplete"
                        }
                      >
                        {currentRound.is_fixed ? "조 편성 완료" : "조 편성 미완료"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* 드롭다운 주석처리 - 가장 가까운 라운드만 표시
          <div className="jo1-dropdown">
            <div className="dropdown-container" onClick={() => setIsDateExpanded((p) => !p)}>
              <div className="dropdown-header">
                <div className="header-content">
                  <div className="date-info">
                    <span className="date-text">
                      {currentRound
                        ? dateFormat(new Date(currentRound.round_date)) + "요일"
                        : "부킹일자를 선택하세요."}
                    </span>
                    {currentRound && (
                      <span className="round-text">
                        {(rounds?.findIndex((r) => r.id === currentRound.id) ?? 0) + 1}회차
                      </span>
                    )}
                  </div>
                  {currentRound && (
                    <div className="status-info">
                      <span
                        className={
                          currentRound.is_fixed
                            ? "status-completed"
                            : currentRound.is_temp_saved
                            ? "status-incomplete"
                            : "status-incomplete"
                        }
                      >
                        {currentRound.is_fixed ? "조 편성 완료" : "조 편성 미완료"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="dropdown-arrow">
                  <div className="arrow-background"></div>
                  <svg
                    className={`arrow-icon ${isDateExpanded ? "expanded" : ""}`}
                    width="9"
                    height="5"
                    viewBox="0 0 10 5"
                    fill="none"
                  >
                    <path
                      d={
                        isDateExpanded
                          ? "M5 2.47226L1.78151 5.86956C1.44745 6.22217 0.885883 6.22217 0.551825 5.86956C0.242354 5.54289 0.242354 5.03126 0.551825 4.70459L4.49875 0.538393C4.77109 0.250921 5.22891 0.250921 5.50125 0.538393L9.44818 4.70459C9.75765 5.03126 9.75765 5.54289 9.44818 5.86956C9.11412 6.22217 8.55255 6.22217 8.21849 5.86956L5 2.47226Z"
                          : "M5 4.52774L1.78151 1.13044C1.44745 0.77783 0.885883 0.77783 0.551825 1.13044C0.242354 1.45711 0.242354 1.96874 0.551825 2.29541L4.49875 6.46161C4.77109 6.74908 5.22891 6.74908 5.50125 6.46161L9.44818 2.29541C9.75765 1.96874 9.75765 1.45711 9.44818 1.13044C9.11412 0.77783 8.55255 0.77783 8.21849 1.13044L5 4.52774Z"
                      }
                      fill="#1A1A1A"
                    />
                  </svg>
                </div>
              </div>

              {isDateExpanded && (
                <div className="dropdown-content" onClick={(e) => e.stopPropagation()}>
                  {(rounds ?? [])
                    .filter((r) => r.id !== currentRound?.id)
                    .map((rd, idx) => {
                      const order = (rounds?.findIndex((r) => r.id === rd.id) ?? idx) + 1;
                      const isCompleted = Boolean(rd.is_fixed);
                      const isTemp = Boolean(rd.is_temp_saved);
                      return (
                        <button
                          key={rd.id}
                          type="button"
                          className="schedule-item"
                          onClick={() => {
                            setSelectedRoundId(rd.id);
                            setIsDateExpanded(false);
                          }}
                        >
                          <div className="date-info">
                            <span className="date-text">
                              {dateFormat(new Date(rd.round_date))}요일
                            </span>
                            <span className="round-text">{order}회차</span>
                          </div>
                          <div className="status-info">
                            <span
                              className={isCompleted ? "status-completed" : "status-incomplete"}
                            >
                              {isCompleted ? "조 편성 완료" : "조 편성 미완료"}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
          */}
          <Divider />
          <div className="team-and-options">
            <div className="as-c" style={{ textAlign: "center" }}>
              {currentRound?.is_fixed ? (
                <>해당 일자 조 편성을 제출하였습니다.</>
              ) : (
                <>
                  {(currentRound?.teams?.length || 0) === 0
                    ? "해당 일자 조 편성이 저장되어 있지 않습니다."
                    : "해당 일자 조 편성이 저장되어 있습니다."}
                  {(currentRound?.teams?.length || 0) > 0 && (
                    <>
                      <br />
                      (저장된 조 편성{" "}
                      <span style={{ color: "#007BFF" }}>{currentRound?.teams?.length}</span>
                      개)
                    </>
                  )}
                </>
              )}
            </div>

            {activeDateIds?.[0] ? (
              <>
                {/* 멤버가 1명일 경우 조편성 불가 메시지 */}
                {(!group?.members || group.members.length < 2) && (
                  <div className="mt-4 fc-red fs-4 text-center">
                    멤버가 1명일 경우 조를 편성할 수 없습니다.
                  </div>
                )}
              </>
            ) : (
              <>
                <div>라운드 일자를 선택하세요.</div>
              </>
            )}
          </div>
        </div>

        {/* 조 편성 미완료일 때 버튼들 */}
        {!currentRound?.is_fixed && group?.members && group.members.length >= 2 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "12px",
              justifyContent: "center",
              alignItems: "center",
              paddingBottom: "12px",
            }}
          >
            {/* 조 편성하기 버튼 - 항상 표시 */}
            <button
              className="easy-register-button"
              onClick={() => {
                openMemberSearchPopup();
              }}
            >
              조 편성하기
            </button>

            {/* 조 제출하기 버튼 - 모든 멤버가 배정되었을 때만 표시 */}
            {isAllMembersAssigned && (
              <button
                className="member-register-button"
                onClick={() => {
                  setShowActionModal(true);
                }}
              >
                조 제출하기
              </button>
            )}
          </div>
        ) : null}

        {/* 조 편성 완료일 때 조편성 보기 버튼 */}
        {currentRound?.is_fixed ? (
          <Button
            className="mt-2"
            $round
            $colorTheme="green"
            style={{ height: "60px" }}
            onClick={() => {
              openMemberSearchPopup();
            }}
          >
            조편성 보기
          </Button>
        ) : null}
      </Container>
      {teams ? (
        <MemeberSearchPopup
          headerMsg="조 편성"
          type="back"
          content={
            <>
              <MemberSearchPopupContents
                isFixed={Boolean(currentRound?.is_fixed)}
                teams={teams}
                closeMe={() => {
                  closeMemberSearchPopup();
                }}
                setTeams={setTeams}
                handleSubmit={handleSubmit}
                selectedDate={
                  currentRound ? dateFormat(new Date(currentRound.round_date)) || "" : ""
                }
              />
            </>
          }
        />
      ) : null}
      <OptionPopup
        headerMsg="라운드 옵션 선택"
        type="back"
        content={
          <YearBookingOptionsPopupContents
            currentRound={currentRound}
            bookingApplication={bookingApplication}
            hasSavedData={hasSavedData}
            activeAwardsIds={activeAwardsIds}
            mealActiveIds={mealActiveIds}
            roomActiveIds={roomActiveIds}
            shopActiveIds={shopActiveIds}
            shopItemValue={shopItemValue}
            handleShopItemValueChange={handleShopItemValueChange}
            etcValue={etcValue}
            handleEtcValue={handleEtcValue}
            closeopenOptionPopup={closeopenOptionPopup}
            handleSubmit={handleSubmit}
            isFixed={Boolean(currentRound?.is_fixed)}
            onAwardsChange={handleAwardsChange}
            onMealChange={handleMealChange}
            onRoomChange={handleRoomChange}
            onProshopChange={handleProshopChange}
          />
        }
      />
      {showAlertModal ? (
        <AlertModal
          title={alertTitle}
          lines={[alertMessage]}
          confirmText="확인"
          onConfirm={() => setShowAlertModal(false)}
          onClose={() => setShowAlertModal(false)}
        />
      ) : null}
      {showActionModal ? (
        <ActionModal
          title="조편성 제출"
          lines={[
            "조 편성과 라운드 옵션을 골프장에 제출하면 추후 수정이 불가합니다. 골프장에 제출하시겠습니까?",
          ]}
          cancelText="취소"
          editText="제출"
          onCancel={() => setShowActionModal(false)}
          onEdit={() => {
            setShowActionModal(false);
            handleSubmit();
          }}
          onClose={() => setShowActionModal(false)}
        />
      ) : null}
    </>
  );
}

const Container = styled.div`
  padding: 16px 12px;
  height: 100%;
  /* Jo1Page Dropdown Styles */
  .jo1-dropdown {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .dropdown-container {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 6px;
    border: 1px solid #d9d9d9;
    background: #fff;
    position: relative;
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
  }
  .dropdown-header {
    display: flex;
    height: 44px;
    width: 100%;
    padding: 12px 10px;
    justify-content: center;
    align-items: flex-start;
    gap: 52px;
    align-self: stretch;
    border-radius: 6px;
    position: relative;
  }
  .header-content {
    position: absolute;
    left: 10px;
    top: 13px;
    width: calc(100% - 20px);
    height: 18px;
    display: flex;
    align-items: center;
    gap: 4px;
    justify-content: space-between;
  }
  .date-info {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
  }
  .date-text {
    font-weight: 700;
    font-size: 14px;
    color: #1a1a1a;
    line-height: 18px;
    min-width: 80px;
    text-align: left;
  }
  .round-text {
    font-weight: 400;
    font-size: 14px;
    color: #1a1a1a;
    line-height: 18px;
    min-width: 50px;
    text-align: left;
  }
  .status-info {
    flex-shrink: 0;
    text-align: right;
  }
  .status-completed {
    font-weight: 700;
    font-size: 14px;
    color: #007bff;
    line-height: 18px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .status-incomplete {
    font-weight: 400;
    font-size: 14px;
    color: #ff4949;
    line-height: 18px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .dropdown-arrow {
    width: 20px;
    height: 20px;
    position: absolute;
    right: 10px;
    top: 12px;
  }
  .arrow-background {
    width: 20px;
    height: 20px;
    background: #d9d9d9;
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 2px;
  }
  .arrow-icon {
    width: 9px;
    height: 8px;
    position: absolute;
    left: 5px;
    top: 6px;
  }
  .dropdown-content {
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  .schedule-item {
    display: flex;
    padding: 13px 10px;
    align-items: center;
    align-self: stretch;
    border-radius: 6px;
    position: relative;
    gap: 4px;
    background: transparent;
    border: 0;
    text-align: left;
    justify-content: space-between;
  }
  .schedule-item .date-info {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
  }
  .schedule-item .date-text {
    font-weight: 400;
    color: #1a1a1a;
    min-width: 80px;
    text-align: left;
  }
  .schedule-item .round-text {
    font-weight: 400;
    color: #1a1a1a;
    min-width: 50px;
    text-align: left;
  }
  .schedule-item .status-info {
    flex-shrink: 0;
    text-align: right;
  }
  .members-container {
    display: flex;
    gap: 10px;
    > .box {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid black;
      width: 50px;
    }
  }
  p.complete {
    display: flex;
    justify-content: center;
    font-weight: bold;
    font-size: 18px;
  }

  .team-and-options {
    display: flex;
    flex-direction: column;
    gap: 24px;
    justify-content: center;
    align-items: center;
    .cover {
      width: calc(100% + 30px);
      height: calc(100% + 10px);
      margin-right: -15px;

      background-color: #00000034;
    }
  }
  .info-notice {
    display: flex;
    padding: 8px 12px;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    background: #dbedff;
    margin: 8px 0;
  }

  .notice-text {
    width: 100% !important;
    text-align: left !important;
    color: #007bff !important;
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    line-height: 150% !important;
    margin: 0 !important;
  }

  /* Button Styles - Member Registration Page Style */
  .easy-register-button {
    flex: 1;
    height: 60px;
    border: 1px solid var(--teeshot-green, #004b49);
    border-radius: 10px;
    background: white;
    color: var(--teeshot-green, #004b49);
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f8f9fa;
    }
  }

  .member-register-button {
    flex: 1;
    height: 60px;
    border: none;
    border-radius: 10px;
    background: var(--teeshot-green, #004b49);
    color: white;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: #003a38;
    }
  }
`;
const ButtonContainer = styled.div`
  color: #007bff;
  border-radius: 100px;
  border: 1px solid #007bff;
  background: #fdfdfd;
  display: flex;
  width: 175px;
  height: 44px;
  gap: 12px;
  padding: 16px;
  align-items: center;
  justify-content: center;
`;
const Text = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  color: #1a1a1a;
`;
